import { useState } from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.status === 200) {
        toast.success('Message sent successfully! We will contact you within 24 hours.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting contact:', error);
      toast.error('Failed to submit message. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <MapPin size={24} />,
      title: 'Address',
      content: '3818 Crenshaw Boulevard Suite 230\nLos Angeles, CA 90008'
    },
    {
      icon: <Phone size={24} />,
      title: 'Phone',
      content: '310-874-5644'
    },
    {
      icon: <Mail size={24} />,
      title: 'Email',
      content: 'info@jrmoralesinvestment.com'
    },
    {
      icon: <Clock size={24} />,
      title: 'Business Hours',
      content: 'Monday - Friday: 9:00 AM - 6:00 PM\nSaturday: By Appointment\nSunday: Closed'
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      <section className="py-24 md:py-32 bg-[#020617]" data-testid="contact-hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h1 className="text-5xl md:text-7xl font-medium tracking-tight leading-none text-[#f8fafc] mb-8" data-testid="contact-title">
            Get in <span className="text-[#d4af37]">Touch</span>
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-slate-300 max-w-3xl mx-auto">
            Ready to start your next real estate project? Contact our experienced team.
          </p>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#0f172a]" data-testid="contact-info-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                data-testid={`contact-info-${index}`}
                className="glass-morphism p-6 text-center"
              >
                <div className="text-[#d4af37] flex justify-center mb-4">
                  {info.icon}
                </div>
                <h3 className="text-lg font-medium text-[#f8fafc] mb-3">
                  {info.title}
                </h3>
                <p className="text-sm text-slate-400 whitespace-pre-line">
                  {info.content}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#020617]" data-testid="contact-form-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="glass-morphism p-8 md:p-12">
            <h2 className="text-3xl md:text-4xl font-normal text-[#f8fafc] mb-8 text-center">
              Send Us a Message
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-slate-300 mb-2 block">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    data-testid="contact-form-name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="bg-slate-950 border-white/10 focus:border-[#d4af37] focus:ring-1 focus:ring-[#d4af37] rounded-sm h-12 text-slate-100"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-slate-300 mb-2 block">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="contact-form-email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="bg-slate-950 border-white/10 focus:border-[#d4af37] focus:ring-1 focus:ring-[#d4af37] rounded-sm h-12 text-slate-100"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone" className="text-slate-300 mb-2 block">Phone Number *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  data-testid="contact-form-phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="bg-slate-950 border-white/10 focus:border-[#d4af37] focus:ring-1 focus:ring-[#d4af37] rounded-sm h-12 text-slate-100"
                  placeholder="(310) 123-4567"
                />
              </div>

              <div>
                <Label htmlFor="message" className="text-slate-300 mb-2 block">Your Message *</Label>
                <Textarea
                  id="message"
                  name="message"
                  data-testid="contact-form-message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={8}
                  className="bg-slate-950 border-white/10 focus:border-[#d4af37] focus:ring-1 focus:ring-[#d4af37] rounded-sm text-slate-100"
                  placeholder="Tell us about your project or any questions you have..."
                />
              </div>

              <Button
                type="submit"
                data-testid="contact-form-submit"
                disabled={isSubmitting}
                className="w-full bg-[#d4af37] text-[#020617] hover:bg-[#b5952f] rounded-sm px-8 py-6 text-lg font-medium transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>

              <p className="text-sm text-slate-400 text-center">
                We will respond to your inquiry within 24 hours.
              </p>
            </form>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#0f172a]" data-testid="map-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="aspect-video w-full overflow-hidden rounded-sm">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3306.2234567890123!2d-118.3359!3d34.0089!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzTCsDAwJzMyLjAiTiAxMTjCsDIwJzA5LjIiVw!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Office Location"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
}